import {useParams, useSearchParams} from "react-router";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import InputForm from "../../../components/input/input-form.jsx";
import Button from "../../../components/button/button.jsx";
import {FaDownload, FaEraser, FaList, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import {useQuery} from "@tanstack/react-query";
import SentryPhoneService from "../../../services/web/sentry-phone-service.js";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import AgGridUtils from "../../../utils/aggrid-utils.js";
import InputDatetime from "../../../components/input/input-datetime.jsx";
import ProjectUtils from "../../../utils/project-utils.js";
import SIncomingCall from "../../../components/icons/incoming-call.jsx";
import SMissedCall from "../../../components/icons/missed-call.jsx";
import SOutgoingCall from "../../../components/icons/outgoing-call.jsx";
import SUnansweredCall from "../../../components/icons/unanswered-call.jsx";
import AudioPlayer from "../../../components/audio-player/audio-player.jsx";
import {useGlobalKey} from "../../../hooks/use-global-key.js";

const initialFilter = {
    callStart: "",
    callEnd: "",
    ramal: "",
    numero: "",
}

const CALL_TYPE_FILTERS = {
    ALL: "",
    INCOMING_ANSWERED: "incoming-answered",
    OUTGOING_ANSWERED: "outgoing-answered",
    INCOMING_MISSED: "incoming-missed",
    OUTGOING_UNANSWERED: "outgoing-unanswered",
};

export default function SentryPhoneCalls() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { sipUser: sipUserParam } = useParams();
    const sipUser = sipUserParam || searchParams.get("sipUser") || "";
    const filterFromUrl = useMemo(() => ({
        ...initialFilter,
        ramal: sipUser,
    }), [sipUser]);
    const [filter, setFilter] = useState(filterFromUrl);
    const [callTypeFilter, setCallTypeFilter] = useState(CALL_TYPE_FILTERS.ALL);
    const [selectedCalls, setSelectedCalls] = useState([]);
    const activeFilterRef = useRef(filterFromUrl);
    const gridRef = useRef(null);
    const gridApi = useRef(null);

    const onGridReady = useCallback((params) => {
        gridApi.current = params.api;
    }, []);

    const onSelectionChanged = useCallback((event) => {
        setSelectedCalls(event.api.getSelectedRows());
    }, []);

    const { data: listAudit = [], isLoading } = useQuery({
        queryKey: ["phones-audit-list", sipUser],
        queryFn: () => SentryPhoneService.listAudit(sipUser || null),
    });

    useEffect(() => {
        setFilter(filterFromUrl);
        activeFilterRef.current = filterFromUrl;

        const api = gridRef.current?.api || gridApi.current;
        if (api) {
            api.onFilterChanged();
        }
    }, [filterFromUrl]);

    const getCallType = (data) => {
        const callReceived = data?.callReceived;
        const callEstablished = data?.callEstablished;

        if (callReceived === true && callEstablished === true) return CALL_TYPE_FILTERS.INCOMING_ANSWERED;
        if (callReceived === false && callEstablished === true) return CALL_TYPE_FILTERS.OUTGOING_ANSWERED;
        if (callReceived === true && callEstablished === false) return CALL_TYPE_FILTERS.INCOMING_MISSED;
        if (callReceived === false && callEstablished === false) return CALL_TYPE_FILTERS.OUTGOING_UNANSWERED;

        return "";
    }

    const getCallIconConfig = (data) => {
        const callReceived = data?.callReceived;
        const callEstablished = data?.callEstablished;

        if (callReceived === true && callEstablished === true) {
            return {
                Icon: SIncomingCall,
                tooltip: "Ligação recebida e atendida",
                className: "text-[#00a118]",
                sortValue: 3,
            };
        }

        if (callReceived === true && callEstablished === false) {
            return {
                Icon: SMissedCall,
                tooltip: "Ligação recebida e não atendida",
                className: "text-[#cb0000]",
                sortValue: 2,
            };
        }

        if (callReceived === false && callEstablished === true) {
            return {
                Icon: SOutgoingCall,
                tooltip: "Ligação efetuada e atendida",
                className: "text-[#00a118]",
                sortValue: 0,
            };
        }

        if (callReceived === false && callEstablished === false) {
            return {
                Icon: SUnansweredCall,
                tooltip: "Ligação efetuada e não atendida",
                className: "text-[#cb0000]",
                sortValue: 1,
            };
        }
    }

    const changeIcon = (data) => {
        const iconConfig = getCallIconConfig(data);
        if (!iconConfig) return "";

        const { Icon, tooltip, className } = iconConfig;

        return (
            <span title={tooltip} className="flex items-center justify-center">
                <Icon className={className} size={16} />
            </span>
        )
    }

    const getCallIconSortValue = (data) => {
        return getCallIconConfig(data)?.sortValue ?? 4;
    }

    const isExternalFilterPresent = useCallback(() => (
        Boolean(
            sipUser
            || activeFilterRef.current.callStart
            || activeFilterRef.current.callEnd
            || activeFilterRef.current.ramal?.trim()
            || activeFilterRef.current.numero?.trim()
        )
    ), [sipUser]);

    const doesExternalFilterPass = useCallback((node) => {
        const data = node.data;
        if (!data) return false;

        const currentFilter = activeFilterRef.current;
        const callStart = data.callStart ? new Date(data.callStart) : null;
        const initialDate = currentFilter.callStart ? new Date(currentFilter.callStart) : null;
        const finalDate = currentFilter.callEnd ? new Date(currentFilter.callEnd) : null;
        const ramal = currentFilter.ramal?.trim();
        const numero = currentFilter.numero?.trim();
        const matchesUrlSipUser = !sipUser || String(data.sipUser ?? "") === sipUser;
        const matchesRamal = !ramal || String(data.sipUser ?? "").includes(ramal);
        const matchesNumero = !numero || String(data.callNumber ?? "").includes(numero);

        if (!matchesUrlSipUser) return false;
        if (!matchesRamal) return false;
        if (!matchesNumero) return false;
        if (initialDate && (!callStart || callStart < initialDate)) return false;

        return !(finalDate && (!callStart || callStart > finalDate));

    }, [sipUser]);

    const handleClear = useCallback(() => {
        setSearchParams({});
        setFilter(initialFilter);
        setCallTypeFilter(CALL_TYPE_FILTERS.ALL);
        activeFilterRef.current = initialFilter;

        const api = gridRef.current?.api || gridApi.current;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, [setSearchParams]);

    const handleSearch = useCallback(() => {
        const api = gridRef.current?.api || gridApi.current;
        if (!api) return;

        activeFilterRef.current = filter;
        api.onFilterChanged();
    }, [filter]);

    const handleCallTypeFilter = useCallback((callType) => {
        const api = gridRef.current?.api || gridApi.current;
        if (!api) return;

        setCallTypeFilter(callType);

        const model = api.getFilterModel() ?? {};

        if (!callType) {
            delete model.callType;
        } else {
            model.callType = {
                filterType: "text",
                type: "equals",
                filter: callType,
            };
        }

        api.setFilterModel(model);
        api.onFilterChanged();
    }, []);

    useGlobalKey("Enter", handleSearch);
    useGlobalKey("Escape", handleClear);

    const buildRecordingFileName = (call) => {
        const number = call?.callNumber ? call.callNumber.replace(/[\\/:*?"<>|]/g, "-") : "desconhecido";

        if (!call?.callStart) {
            return `${number}_data-desconhecida.mp3`;
        }

        const date = new Date(call.callStart);

        const formattedDate = [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, "0"),
            String(date.getDate()).padStart(2, "0"),
        ].join("-");

        const formattedTime = [
            String(date.getHours()).padStart(2, "0"),
            String(date.getMinutes()).padStart(2, "0"),
            String(date.getSeconds()).padStart(2, "0"),
        ].join("-");

        return `${number}_${formattedDate}_${formattedTime}.mp3`;
    }

    const columnDefs = useMemo(() => ([
        {
            headerName: "Ramal/Usuário",
            field: "sipUser",
            minWidth: 120,
            width: 150,
            maxWidth: 160,
            flex: 0.7,
        },
        {
            headerName: "D/H início",
            field: "callStart",
            minWidth: 135,
            width: 135,
            valueFormatter: ({ value }) => ProjectUtils.formatDate(value),
        },
        {
            headerName: "D/H término",
            field: "callEnd",
            minWidth: 135,
            width: 135,
            valueFormatter: ({ value }) => ProjectUtils.formatDate(value),
        },
        {
            headerName: "Duração",
            field: "duration",
            minWidth: 120,
            width: 130,
            valueFormatter: ({ value }) => ProjectUtils.formatMillisecondsToDuration(value),
            comparator: AgGridUtils.nullLastComparator,
        },
        {
            headerName: "Tipo",
            field: "callReceived",
            width: 48,
            minWidth: 48,
            maxWidth: 48,
            suppressSizeToFit: true,
            cellClass: "!p-0 !flex !items-center !justify-center",
            valueGetter: ({ data }) => getCallIconSortValue(data),
            cellRenderer: ({ data }) => changeIcon(data),
        },
        {
            headerName: "Tipo de chamada",
            colId: "callType",
            valueGetter: ({ data }) => getCallType(data),
            filter: "agTextColumnFilter",
            hide: true,
        },
        {
            headerName: "Número",
            field: "callNumber",
            minWidth: 150,
            width: 165,
            valueFormatter: ({ value }) => ProjectUtils.formatPhone(value),
        },
        {
            headerName: "Nome do contato",
            field: "contactName",
            minWidth: 200,
            flex: 1,
        },
        {
            headerName: "ID atendimento",
            field: "attendanceId",
            width: 90,
            minWidth: 120,
            maxWidth: 120,
            cellRenderer: ({ data, value }) => {
                const attendanceId = data?.attendanceId;

                if (!attendanceId) return "";

                return (
                    <button
                        type="button"
                        className="cursor-pointer"
                        onClick={() => window.open(SentryPhoneService.getAttendanceUrl(attendanceId), "_blank")}
                    >
                        {value}
                    </button>
                );
            },
        },
        {
            headerName: "Gravação",
            field: "callAttachment",
            minWidth: 220,
            width: 280,
            flex: 1.2,
            cellClass: "!p-1 !flex !items-center",
            cellRenderer: ({ data }) => {
                if (!data?.callAttachment) {
                    return (
                        <span className="italic text-[#8F8F8F]">Sem gravação</span>
                    )
                }

                const fileName = buildRecordingFileName(data);

                return <AudioPlayer src={SentryPhoneService.getAudio(data.callAttachment, fileName)} />;
            },
        },
    ]), []);

    const rowSelection = useMemo(() => ({
        mode: "multiRow",
        checkboxes: (params) => Boolean(params.data?.callAttachment),
        headerCheckbox: true,
        enableClickSelection: false,
        isRowSelectable: (node) => Boolean(node.data?.callAttachment),
    }), []);

    const selectionColumnDef = useMemo(() => ({
        cellStyle: { paddingLeft: 8 },
        headerStyle: { paddingLeft: 16 },
    }), []);

    const handleDownloadCall = async () => {
        try {
            const callIds = selectedCalls.map(call => call.id);
            const blob = await SentryPhoneService.downloadRecording(callIds);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.href = url;
            link.download = "gravacoes-sentry-phone.zip";

            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(url);
        } catch (error) {
            let errorData = error.response?.data;

            if (errorData instanceof Blob) {
                const text = await errorData.text();
                errorData = JSON.parse(text);
            }

            const errorMessages = {
                "phone.recordings.download.empty": "Selecione ao menos uma gravação para realizar o download.",
                "phone.recordings.download.limit": "É permitido baixar no máximo 500 gravações por vez.",
                "phone.recordings.download.unavailable": "Uma ou mais gravações selecionadas não estão disponíveis.",
            };

            const message = errorMessages[errorData?.message] ?? "Não foi possível baixar a gravação";

            ProjectUtils.swalErrorMessage("", message, "Ok");
        }
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>{sipUser ? sipUser : "Registros de chamadas"}</Template.Title>
            </Template.Header>
            <Template.Body className="space-y-2">
                <Card>
                    <Card.Body className="space-y-2">
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-4 flex items-stretch">
                                <InputDatetime
                                    id="callStart"
                                    label="Período entre"
                                    selected={filter.callStart}
                                    onChange={(value) => setFilter(prev => ({ ...prev, callStart: value }))}
                                    customIcon
                                    inputClass="!rounded-e-none"
                                    classNameWrapper="w-[47%]"
                                />
                                <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>
                                <InputDatetime
                                    id="callEnd"
                                    selected={filter.callEnd}
                                    onChange={(value) => setFilter(prev => ({ ...prev, callEnd: value }))}
                                    customIcon
                                    inputClass="!rounded-s-none"
                                    classNameWrapper="w-[47%]"
                                />
                            </div>
                            <InputForm
                                id="ramal"
                                label="Ramal"
                                value={filter.ramal}
                                onChange={({target: {value}}) => setFilter(prev => ({ ...prev, ramal: value }))}
                                classNameWrapper="col-span-4"
                            />
                            <InputForm
                                id="numero"
                                label="Número"
                                value={filter.numero}
                                onChange={({target: {value}}) => setFilter(prev => ({ ...prev, numero: value }))}
                                classNameWrapper="col-span-4"
                            />
                        </div>
                        <div className="flex justify-between">
                            <div className="flex gap-2">
                                <Button
                                    target={callTypeFilter === CALL_TYPE_FILTERS.ALL ? "primary" : "primary-outline"}
                                    onClick={() => handleCallTypeFilter(CALL_TYPE_FILTERS.ALL)}
                                >
                                    <FaList />
                                    Todas
                                </Button>
                                <Button
                                    target={callTypeFilter === CALL_TYPE_FILTERS.INCOMING_ANSWERED ? "primary" : "primary-outline"}
                                    className="gap-2"
                                    onClick={() => handleCallTypeFilter(CALL_TYPE_FILTERS.INCOMING_ANSWERED)}
                                >
                                    <SIncomingCall size={12} className="text-[#00a118]" />
                                    Recebida atendida
                                </Button>
                                <Button
                                    target={callTypeFilter === CALL_TYPE_FILTERS.OUTGOING_ANSWERED ? "primary" : "primary-outline"}
                                    className="gap-2"
                                    onClick={() => handleCallTypeFilter(CALL_TYPE_FILTERS.OUTGOING_ANSWERED)}
                                >
                                    <SOutgoingCall size={12} className="text-[#00a118]" />
                                    Efetuada atendida
                                </Button>
                                <Button
                                    target={callTypeFilter === CALL_TYPE_FILTERS.INCOMING_MISSED ? "primary" : "primary-outline"}
                                    className="gap-2"
                                    onClick={() => handleCallTypeFilter(CALL_TYPE_FILTERS.INCOMING_MISSED)}
                                >
                                    <SMissedCall size={12} className="text-[#cb0000]" />
                                    Recebida não atendida
                                </Button>
                                <Button
                                    target={callTypeFilter === CALL_TYPE_FILTERS.OUTGOING_UNANSWERED ? "primary" : "primary-outline"}
                                    className="gap-2"
                                    onClick={() => handleCallTypeFilter(CALL_TYPE_FILTERS.OUTGOING_UNANSWERED)}
                                >
                                    <SUnansweredCall size={12} className="text-[#cb0000]" />
                                    Efetuada não atendida
                                </Button>
                            </div>
                            <div className="flex gap-2 items-end">
                                <Button
                                    disabled={selectedCalls.length === 0}
                                    title={selectedCalls.length > 1 ? "Baixar gravações" : "Baixar gravação"}
                                    onClick={handleDownloadCall}
                                >
                                    <FaDownload />
                                </Button>
                                <Button
                                    title="Limpar"
                                    target="primary-outline"
                                    onClick={handleClear}
                                >
                                    <FaEraser />
                                </Button>
                                <Button
                                    title="Buscar"
                                    onClick={handleSearch}
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-192px)]">
                            <AgGridReact
                                onGridReady={onGridReady}
                                onSelectionChanged={onSelectionChanged}
                                ref={gridRef}
                                rowData={listAudit}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                isExternalFilterPresent={isExternalFilterPresent}
                                doesExternalFilterPass={doesExternalFilterPass}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                rowSelection={rowSelection}
                                selectionColumnDef={selectionColumnDef}
                                suppressCellFocus={true}
                                animateRows={false}
                                gridOptions={{ localeText: AgGridUtils.localization() }}
                                theme={AgGridUtils.generalTheme({ rowHeight: 38 })}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}
